import { environment } from '../../environments/environment';
import type { CoreUserProfile } from '../models/profile-api.types';

const DEFAULT_PROFILE = '/assets/images/user-profile.jpeg';

function recordString(
  record: Record<string, unknown>,
  key: string,
): string {
  const value = record[key];
  return typeof value === 'string' ? value.trim() : '';
}

export function resolveUserDisplayName(
  user: Record<string, unknown> | CoreUserProfile | null | undefined,
): string {
  if (!user) return 'User';
  const record = user as Record<string, unknown>;
  const first = recordString(record, 'first_name');
  const last = recordString(record, 'last_name');
  const full = `${first} ${last}`.trim();
  if (full) return full;
  const display = recordString(record, 'display_name');
  if (display) return display;
  const partnerName = recordString(record, 'partnerLegalName');
  if (partnerName) return partnerName;
  const tradeName = recordString(record, 'partnerTradeName');
  if (tradeName) return tradeName;
  const email = recordString(record, 'email');
  return email || 'User';
}

export function resolveProfilePhotoUrl(
  user: Pick<CoreUserProfile, 'profile_image' | 'default_avatar'> | null | undefined,
): string {
  if (!user) return DEFAULT_PROFILE;
  const api = environment.apiUrl.replace(/\/$/, '');
  const filename = (user.profile_image ?? '').trim();
  if (filename && !filename.includes('/')) {
    return `${api}/profile-images/${encodeURIComponent(filename)}`;
  }
  const avatar = (user.default_avatar ?? '').trim();
  if (avatar) {
    if (avatar.startsWith('http://') || avatar.startsWith('https://')) {
      return avatar;
    }
    return avatar.startsWith('/') ? avatar : `/assets/images/${avatar}`;
  }
  return DEFAULT_PROFILE;
}

export function splitPhoneParts(raw: string | null | undefined): {
  countryCode: string;
  contactNumber: string;
} {
  const text = String(raw ?? '').trim();
  if (!text) {
    return { countryCode: '+91', contactNumber: '' };
  }
  const digits = text.replaceAll(/\D/g, '');
  if (digits.length === 12 && digits.startsWith('91')) {
    return { countryCode: '+91', contactNumber: digits.slice(2) };
  }
  if (digits.length === 10) {
    return { countryCode: '+91', contactNumber: digits };
  }
  if (text.startsWith('+')) {
    const spaceIdx = text.search(/\s/);
    if (spaceIdx > 1) {
      const code = text.slice(0, spaceIdx);
      if (/^\+\d{1,3}$/.test(code)) {
        return {
          countryCode: code,
          contactNumber: text.slice(spaceIdx).replaceAll(/\D/g, ''),
        };
      }
    } else if (/^\+\d{1,3}$/.test(text)) {
      return { countryCode: text, contactNumber: '' };
    }
  }
  return { countryCode: '+91', contactNumber: digits };
}
